/* Maison Sabelle header: mega menu, compact sticky morph and mobile drawer.
   <sabelle-header> wraps sections/sabelle-header.liquid. No dependencies.
   Horizon scrolls .page-wrapper on desktop (html and body are overflow hidden), and the window below 990px,
   so scroll is read from both. */
(() => {
  if (customElements.get('sabelle-header')) return;

  const DESKTOP = '(min-width: 990px)';
  const OPEN_DELAY = 140; // hover intent before the first panel opens
  const SWITCH_DELAY = 70; // moving between triggers while a panel is already open
  const CLOSE_DELAY = 280; // grace period after the pointer leaves the header
  const COMPACT_AT = 140; // px scrolled before the header becomes the compact row
  const EXPAND_AT = 36; // px from the top before it opens back up
  const MORPH_MS = 650;
  const FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

  class SabelleHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.bar = this.querySelector('.sbh-bar');
      this.head = this.querySelector('.sbh-head');
      this.sheet = this.querySelector('.sbh-sheet');
      this.drawer = this.querySelector('.sbh-drawer');
      this.burger = this.querySelector('.sbh-burger');
      this.triggers = Array.from(this.querySelectorAll('[data-panel-trigger]'));
      this.topLinks = Array.from(this.querySelectorAll('.sbh-nav__link'));
      this.panels = this.triggers.map((t) => this.panelOf(t)).filter(Boolean);
      this.desktop = window.matchMedia(DESKTOP);
      this.sticky = this.dataset.sticky || 'compact';
      this.wrapper = document.querySelector('.page-wrapper');
      this.current = null;
      this.compact = false;
      this.drawerOpen = false;
      this.timers = {};
      this.lastPointer = 'mouse';
      this.ac = new AbortController();

      const o = { signal: this.ac.signal };
      this.bindMenu(o);
      this.bindDrawer(o);
      this.bindScroll(o);
      this.bindEditor(o);
    }

    disconnectedCallback() {
      this.ac.abort();
      if (this.ro) this.ro.disconnect();
      Object.values(this.timers).forEach(clearTimeout);
      if (this.drawerOpen) document.documentElement.removeAttribute('scroll-lock');
    }

    /* ---------- timers ---------- */
    later(name, fn, ms) {
      clearTimeout(this.timers[name]);
      this.timers[name] = setTimeout(fn, ms);
    }

    clear(name) {
      clearTimeout(this.timers[name]);
    }

    /* ---------- mega menu ---------- */
    panelOf(trigger) {
      const id = trigger && trigger.getAttribute('aria-controls');
      return id ? document.getElementById(id) : null;
    }

    bindMenu(o) {
      const isMouse = (e) => e.pointerType === 'mouse' || e.pointerType === 'pen';

      this.addEventListener('pointerdown', (e) => (this.lastPointer = e.pointerType), { ...o, capture: true });

      this.triggers.forEach((t) => {
        t.addEventListener(
          'pointerenter',
          (e) => {
            if (!isMouse(e) || !this.desktop.matches) return;
            this.clear('close');
            if (this.current === t) return;
            this.later('open', () => this.open(t), this.current ? SWITCH_DELAY : OPEN_DELAY);
          },
          o
        );
        t.addEventListener('pointerleave', () => this.clear('open'), o);

        // Touch: the first tap opens the panel, the second follows the link.
        t.addEventListener(
          'click',
          (e) => {
            if (!this.desktop.matches || this.lastPointer !== 'touch' || this.current === t) return;
            e.preventDefault();
            this.open(t);
          },
          o
        );

        // Keyboard focus previews the panel; its links follow the trigger in the tab order.
        t.addEventListener(
          'focus',
          () => {
            if (!this.desktop.matches || this.suppressFocus) return;
            if (t.matches(':focus-visible')) this.open(t);
          },
          o
        );
      });

      this.topLinks.forEach((link) => {
        link.addEventListener('keydown', (e) => this.onTopKey(e, link), o);
        if (!link.hasAttribute('data-panel-trigger')) {
          link.addEventListener(
            'focus',
            () => {
              if (this.current) this.close();
            },
            o
          );
        }
      });

      // Leaving the header (the bar plus its panels) closes after a short grace period.
      this.bar.addEventListener(
        'pointerleave',
        (e) => {
          if (!isMouse(e) || !this.current || this.pinned) return;
          this.clear('open');
          this.later('close', () => this.close(), CLOSE_DELAY);
        },
        o
      );
      this.bar.addEventListener('pointerenter', () => this.clear('close'), o);

      // Plain items and the icons close an open panel; the wordmark is a bridge between the two navs.
      const closers = [
        [this.querySelectorAll('.sbh-nav__item:not(.has-panel) > .sbh-nav__link, .sbh-icons'), 160],
        [this.querySelectorAll('.sbh-word'), 380],
      ];
      closers.forEach(([els, ms]) =>
        els.forEach((el) =>
          el.addEventListener(
            'pointerenter',
            (e) => {
              if (!isMouse(e) || !this.current || this.pinned) return;
              this.clear('open');
              this.later('close', () => this.close(), ms);
            },
            o
          )
        )
      );

      this.bar.addEventListener(
        'focusout',
        (e) => {
          const to = e.relatedTarget;
          if (!this.current || !to) return;
          const panel = this.panelOf(this.current);
          if (to === this.current || (panel && panel.contains(to)) || this.triggers.includes(to)) return;
          this.close();
        },
        o
      );

      document.addEventListener(
        'pointerdown',
        (e) => {
          if (this.current && !this.bar.contains(e.target)) this.close();
        },
        o
      );

      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Escape') return;
          if (this.drawerOpen) {
            this.closeDrawer();
          } else if (this.current) {
            const t = this.current;
            this.close();
            this.focusQuietly(t);
          }
        },
        o
      );

      this.desktop.addEventListener(
        'change',
        () => {
          this.close();
          if (this.desktop.matches) this.closeDrawer(true);
        },
        o
      );
    }

    onTopKey(e, link) {
      const i = this.topLinks.indexOf(link);
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const step = e.key === 'ArrowRight' ? 1 : -1;
        this.topLinks[(i + step + this.topLinks.length) % this.topLinks.length].focus();
      } else if ((e.key === 'ArrowDown' || e.key === ' ') && link.hasAttribute('data-panel-trigger')) {
        e.preventDefault();
        this.open(link);
        const panel = this.panelOf(link);
        const first = panel && panel.querySelector('a[href]:not([tabindex="-1"])');
        if (first) setTimeout(() => first.focus(), 30);
      }
    }

    focusQuietly(el) {
      this.suppressFocus = true;
      el.focus();
      this.suppressFocus = false;
    }

    open(t) {
      this.clear('open');
      this.clear('close');
      if (this.current === t) return;
      const panel = this.panelOf(t);
      if (!panel) return;
      const switching = Boolean(this.current);
      if (switching) {
        this.current.setAttribute('aria-expanded', 'false');
        const prev = this.panelOf(this.current);
        if (prev) prev.classList.remove('is-open', 'is-quick');
      }
      this.current = t;
      t.setAttribute('aria-expanded', 'true');
      panel.classList.toggle('is-quick', switching);
      panel.classList.add('is-open');
      this.classList.add('has-open');
      this.sizeSheet();
    }

    close() {
      this.clear('open');
      this.clear('close');
      if (!this.current) return;
      this.current.setAttribute('aria-expanded', 'false');
      const panel = this.panelOf(this.current);
      if (panel) panel.classList.remove('is-open', 'is-quick');
      this.current = null;
      this.pinned = false;
      this.classList.remove('has-open');
      if (this.sheet) this.sheet.style.height = '0px';
    }

    sizeSheet() {
      const panel = this.current && this.panelOf(this.current);
      if (panel && this.sheet) this.sheet.style.height = panel.offsetHeight + 'px';
    }

    /* ---------- compact sticky morph ---------- */
    bindScroll(o) {
      const onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.updateScroll();
        });
      };
      document.addEventListener('scroll', onScroll, { ...o, capture: true, passive: true });
      window.addEventListener('scroll', onScroll, { ...o, passive: true });

      // Reserve the expanded height on the section, so the morph never moves the page underneath.
      this.measure();
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.bar);
      this.panels.forEach((p) => this.ro.observe(p));

      if (this.scrollY() > COMPACT_AT && this.sticky === 'compact') {
        this.classList.add('sbh--instant');
        this.updateScroll();
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('sbh--instant')));
      } else {
        this.updateScroll();
      }
    }

    scrollY() {
      const w = this.wrapper;
      return Math.max(window.scrollY || 0, w ? w.scrollTop : 0);
    }

    updateScroll() {
      const y = this.scrollY();
      this.classList.toggle('is-scrolled', y > 2);
      if (this.sticky !== 'compact') return;
      const next = this.compact ? y > EXPAND_AT : y > COMPACT_AT;
      if (next !== this.compact) this.setCompact(next);
    }

    setCompact(on) {
      this.compact = on;
      this.morphing = true;
      this.later('morph', () => {
        this.morphing = false;
        this.measure();
      }, MORPH_MS);
      this.classList.toggle('is-compact', on);
    }

    measure() {
      const barH = this.bar.offsetHeight;
      if (!this.compact && !this.morphing && this.section) {
        this.section.style.setProperty('--sbh-reserve', barH + 'px');
      }
      const headH = this.head.offsetHeight;
      this.style.setProperty('--sbh-head-h', headH + 'px');
      document.body.style.setProperty('--header-height', headH + 'px');
      document.body.style.setProperty('--header-group-height', barH + 'px');
      this.sizeSheet();
    }

    /* ---------- mobile drawer ---------- */
    bindDrawer(o) {
      if (!this.drawer || !this.burger) return;
      this.burger.addEventListener('click', () => this.openDrawer(), o);
      this.drawer
        .querySelectorAll('[data-drawer-close]')
        .forEach((el) => el.addEventListener('click', () => this.closeDrawer(), o));
      this.drawer.addEventListener('keydown', (e) => this.trapFocus(e), o);
      this.drawer.querySelectorAll('.sbh-acc__btn').forEach((btn) =>
        btn.addEventListener(
          'click',
          () => {
            const item = btn.closest('.sbh-acc');
            const open = !item.classList.contains('is-open');
            item.classList.toggle('is-open', open);
            btn.setAttribute('aria-expanded', String(open));
          },
          o
        )
      );
    }

    openDrawer() {
      if (this.drawerOpen) return;
      this.drawerOpen = true;
      this.drawer.classList.add('is-open');
      this.burger.setAttribute('aria-expanded', 'true');
      document.documentElement.setAttribute('scroll-lock', '');
      const closeBtn = this.drawer.querySelector('.sbh-drawer__head [data-drawer-close]');
      if (closeBtn) setTimeout(() => closeBtn.focus({ preventScroll: true }), 60);
    }

    closeDrawer(silent) {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;
      this.drawer.classList.remove('is-open');
      this.burger.setAttribute('aria-expanded', 'false');
      document.documentElement.removeAttribute('scroll-lock');
      if (!silent) this.burger.focus({ preventScroll: true });
    }

    trapFocus(e) {
      if (e.key !== 'Tab') return;
      const items = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden'
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------- theme editor: selecting a "Mega panel" block opens it ---------- */
    bindEditor(o) {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener(
        'shopify:block:select',
        (e) => {
          const panel = e.target && e.target.closest && e.target.closest('.sbh-panel');
          if (!panel || !this.contains(panel)) return;
          const t = this.triggers.find((x) => x.getAttribute('aria-controls') === panel.id);
          if (t && this.desktop.matches) {
            this.open(t);
            this.pinned = true;
          }
        },
        o
      );
      document.addEventListener(
        'shopify:block:deselect',
        (e) => {
          if (e.target && this.contains(e.target)) this.close();
        },
        o
      );
    }
  }

  customElements.define('sabelle-header', SabelleHeader);
})();
